// <sql-highlight> — SQL syntax highlighting as a light-DOM enhancement, in
// two modes decided by what it wraps:
//
//   Static (the show page):
//     <sql-highlight><pre><code>SELECT ...</code></pre></sql-highlight>
//     The code's text is tokenized and re-rendered in place.
//
//   Editor (the extraction query page):
//     <sql-highlight><textarea>SELECT ...</textarea></sql-highlight>
//     The textarea stays the value holder — native form participation, undo,
//     IME, and mobile input all intact (the reason this is an overlay and not
//     contentEditable). Its text goes transparent (caret kept visible) and a
//     highlighted <pre> mirrors it exactly behind: same font metrics, padding
//     and transparent border copied from computed style, content synced on
//     input, scroll synced on scroll. Without JS the plain textarea renders
//     and submits as-is.
//
// The tokenizer is a single-pass regex — comments, strings (with '' escapes),
// quoted identifiers, numbers, keywords/types, function calls — plus the
// {{file}} template placeholder, highlighted even inside string literals
// since it's load-bearing in extraction queries.

const KEYWORDS = new Set([
	"SELECT",
	"FROM",
	"WHERE",
	"AS",
	"AND",
	"OR",
	"NOT",
	"NULL",
	"IS",
	"IN",
	"LIKE",
	"ILIKE",
	"BETWEEN",
	"CASE",
	"WHEN",
	"THEN",
	"ELSE",
	"END",
	"ON",
	"JOIN",
	"LEFT",
	"RIGHT",
	"INNER",
	"OUTER",
	"CROSS",
	"GROUP",
	"BY",
	"ORDER",
	"HAVING",
	"LIMIT",
	"OFFSET",
	"UNION",
	"ALL",
	"DISTINCT",
	"OVER",
	"PARTITION",
	"WITH",
	"CAST",
	"TRY_CAST",
	"TRUE",
	"FALSE",
	"ASC",
	"DESC",
	"EXCLUDE",
	"DECIMAL",
	"INTEGER",
	"BIGINT",
	"DOUBLE",
	"BOOLEAN",
	"VARCHAR",
	"TEXT",
	"DATE",
	"TIMESTAMP",
]);

const CLS = {
	keyword: "font-medium text-info",
	fn: "text-white/90",
	string: "text-positive",
	ident: "text-white/85",
	number: "text-accent",
	comment: "italic text-white/35",
	placeholder: "rounded bg-accent/15 px-0.5 text-accent",
};

// Alternation order is the precedence: comments, strings, quoted identifiers,
// the {{placeholder}}, numbers, function calls (word before a paren), words.
const TOKENS =
	/(--[^\n]*|\/\*[\s\S]*?\*\/)|('(?:[^']|'')*'?)|("(?:[^"]|"")*"?)|(\{\{\s*\w+\s*\}\})|(\b\d+(?:\.\d+)?\b)|([A-Za-z_][A-Za-z0-9_]*(?=\s*\())|([A-Za-z_][A-Za-z0-9_]*)/g;

const HTML_ESCAPES: Record<string, string> = {
	"&": "&amp;",
	"<": "&lt;",
	">": "&gt;",
	'"': "&quot;",
	"'": "&#39;",
};

function escapeHtml(text: string): string {
	return text.replace(/[&<>"']/g, (c) => HTML_ESCAPES[c] ?? c);
}

function span(cls: string, escaped: string): string {
	return `<span class="${cls}">${escaped}</span>`;
}

function highlightPlaceholders(escaped: string): string {
	return escaped.replace(/\{\{\s*\w+\s*\}\}/g, (m) => span(CLS.placeholder, m));
}

export function highlightSql(sql: string): string {
	let html = "";
	let last = 0;
	TOKENS.lastIndex = 0;

	for (let match = TOKENS.exec(sql); match; match = TOKENS.exec(sql)) {
		html += escapeHtml(sql.slice(last, match.index));
		last = match.index + match[0].length;
		const [, comment, string, ident, placeholder, number, fn, word] = match;

		if (comment) html += span(CLS.comment, escapeHtml(comment));
		else if (string)
			html += span(CLS.string, highlightPlaceholders(escapeHtml(string)));
		else if (ident) html += span(CLS.ident, escapeHtml(ident));
		else if (placeholder)
			html += span(CLS.placeholder, escapeHtml(placeholder));
		else if (number) html += span(CLS.number, escapeHtml(number));
		else if (fn)
			html += span(
				KEYWORDS.has(fn.toUpperCase()) ? CLS.keyword : CLS.fn,
				escapeHtml(fn),
			);
		else if (word)
			html += KEYWORDS.has(word.toUpperCase())
				? span(CLS.keyword, escapeHtml(word))
				: escapeHtml(word);
	}

	return html + escapeHtml(sql.slice(last));
}

class SqlHighlight extends HTMLElement {
	#abort?: AbortController;

	connectedCallback() {
		this.#abort?.abort(); // idempotent under Turbo restore
		this.#abort = new AbortController();

		const textarea = this.querySelector("textarea");
		if (textarea) {
			this.#enhanceEditor(textarea);
		} else {
			this.#enhanceStatic();
		}
	}

	disconnectedCallback() {
		this.#abort?.abort();
	}

	// Static mode: re-render the code's own text. Naturally idempotent —
	// textContent of a highlighted block is the original SQL.
	#enhanceStatic() {
		const code = this.querySelector("code") ?? this.querySelector("pre");
		if (!code) return;
		code.innerHTML = highlightSql(code.textContent ?? "");
	}

	#enhanceEditor(textarea: HTMLTextAreaElement) {
		this.style.display = "block";
		this.style.position = "relative";

		// Rebuild-if-missing (Turbo may restore a cached copy with it present).
		let backdrop = this.querySelector<HTMLPreElement>(
			"[data-sql-highlight-backdrop]",
		);
		if (!backdrop) {
			backdrop = document.createElement("pre");
			backdrop.dataset.sqlHighlightBackdrop = "";
			backdrop.setAttribute("aria-hidden", "true");
			backdrop.appendChild(document.createElement("code"));
			this.prepend(backdrop);
		}
		const backdropEl = backdrop;
		const code = backdropEl.querySelector("code");
		if (!code) return;

		// Mirror the textarea's box and text metrics exactly; the backdrop takes
		// over the background, the textarea keeps its border and focus ring on
		// top with transparent text and a visible caret.
		const computed = getComputedStyle(textarea);
		Object.assign(backdropEl.style, {
			position: "absolute",
			inset: "0",
			margin: "0",
			overflow: "hidden",
			pointerEvents: "none",
			zIndex: "0",
			whiteSpace: "pre-wrap",
			overflowWrap: "break-word",
			fontFamily: computed.fontFamily,
			fontSize: computed.fontSize,
			fontWeight: computed.fontWeight,
			lineHeight: computed.lineHeight,
			letterSpacing: computed.letterSpacing,
			padding: computed.padding,
			borderWidth: computed.borderWidth,
			borderStyle: "solid",
			borderColor: "transparent",
			borderRadius: computed.borderRadius,
			backgroundColor: computed.backgroundColor,
			color: computed.color,
		});
		code.style.font = "inherit";

		Object.assign(textarea.style, {
			position: "relative",
			zIndex: "1",
			backgroundColor: "transparent",
			color: "transparent",
			caretColor: computed.color,
		});

		const abort = this.#abort;
		if (!abort) return;
		const { signal } = abort;

		const render = () => {
			const value = textarea.value;
			if (value) {
				// A trailing newline collapses in <pre> but not in a textarea — the
				// sentinel space keeps the final line's height in the mirror.
				code.innerHTML = highlightSql(
					value.endsWith("\n") ? `${value} ` : value,
				);
			} else {
				code.innerHTML = span(
					"text-white/30",
					escapeHtml(textarea.placeholder ?? ""),
				);
			}
			backdropEl.scrollTop = textarea.scrollTop;
			backdropEl.scrollLeft = textarea.scrollLeft;
		};

		textarea.addEventListener("input", render, { signal });
		textarea.addEventListener(
			"scroll",
			() => {
				backdropEl.scrollTop = textarea.scrollTop;
				backdropEl.scrollLeft = textarea.scrollLeft;
			},
			{ signal },
		);

		render();
	}
}

if (!customElements.get("sql-highlight")) {
	customElements.define("sql-highlight", SqlHighlight);
}
